/**
 * The shipped-but-not-received rule for a transfer line, in ONE copy.
 *
 * The backend's completion gate, receive cap, cancel return and incoming
 * breakdown read it, and so do the receiving card and the complete button, so
 * what the button allows and what the server accepts cannot drift apart.
 */
/**
 * Shipped units on a line that an operator resolved as never arriving
 * (returned to source or scrapped as lost in transit). Zero until the line is
 * resolved. Every piece of shipped arithmetic that means "units still owed to
 * the destination" subtracts this.
 */
export function resolvedUnits(item) {
    return item.unreceived_resolved_at ? (item.unreceived_quantity ?? 0) : 0;
}
/**
 * Units of a line that left the source and are still owed to the destination:
 * shipped minus received minus resolved, never below zero.
 *
 * Arithmetic, not a resolved flag: a resolved line whose received count later
 * dropped has units in transit again rather than reading as settled.
 */
export function inTransitUnits(item) {
    return Math.max((item.shipped_quantity ?? 0) - (item.received_quantity ?? 0) - resolvedUnits(item), 0);
}
/**
 * The lines of a shipped transfer that still have units in transit.
 *
 * The second half of the completion gate. `unconfirmedShortfallLines` covers
 * ordered minus shipped, the units that never left the source; this covers the
 * units that DID leave and have not arrived. Finishing a transfer while those
 * are outstanding strands them: the source already decremented them and the
 * destination never increments them, so they vanish from the books.
 *
 * Stamped orders only (`shipped_quantities_recorded_at`): only a per SKU ship
 * writes `shipped_quantity` literally, and an unstamped legacy transfer must
 * behave exactly as it did.
 */
export function unreceivedShippedLines(items, shippedQuantitiesRecordedAt) {
    if (!shippedQuantitiesRecordedAt)
        return [];
    return items.filter((item) => inTransitUnits(item) > 0);
}
